import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useLazyGetDitioTokenQuery } from "apis/dataV2/dataV2Api";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { featuresConfig } from "config/features";
import { useSceneId } from "hooks/useSceneId";
import { selectEnabledWidgets } from "slices/explorer";
import { telemetry } from "telemetry";
import { AsyncStatus } from "types/misc";
import { secondsToMs } from "utils/time";

import { ditioActions, selectDitioAccessToken } from "../slice";

export function useHandleDitioAuth() {
    const { t } = useTranslation();
    const sceneId = useSceneId();
    const dispatch = useAppDispatch();
    const enabled = useAppSelector(selectEnabledWidgets).some((widget) => widget.key === featuresConfig.ditio.key);
    const token = useAppSelector(selectDitioAccessToken);
    const [triggerAuthQuery] = useLazyGetDitioTokenQuery();
    const timeoutId = useRef<number>();

    const authenticate = useCallback(async () => {
        const res = await triggerAuthQuery({ projectId: sceneId })
            .unwrap()
            .catch((e) => {
                console.warn("Ditio machines authentication failed");
                telemetry.trackCaughtException({
                    source: "ditio.useHandleDitioAuth.authenticate",
                    category: "integration",
                    error: e,
                });
                return undefined;
            });

        if (res?.access_token) {
            dispatch(
                ditioActions.setAccessToken({
                    status: AsyncStatus.Success,
                    data: {
                        token: res.access_token,
                        refreshIn: res.expires_in,
                    },
                }),
            );
        } else {
            dispatch(
                ditioActions.setAccessToken({
                    status: AsyncStatus.Error,
                    msg: t("ditioAuthError"),
                }),
            );
        }
    }, [triggerAuthQuery, sceneId, dispatch, t]);

    useEffect(() => {
        if (token.status !== AsyncStatus.Initial || !enabled) {
            return;
        }

        dispatch(ditioActions.setAccessToken({ status: AsyncStatus.Loading }));
        authenticate();
    }, [token, dispatch, authenticate, enabled]);

    useEffect(() => {
        if (token.status !== AsyncStatus.Success) {
            return;
        }

        timeoutId.current = window.setInterval(
            () => {
                authenticate();

                // NOTE(OLA): A bug in Ditio sometimes returns a very high expiry time
            },
            (token.data.refreshIn > 3600 * 24 ? token.data.refreshIn : secondsToMs(token.data.refreshIn)) * 0.9,
        );

        return () => {
            clearTimeout(timeoutId.current);
            timeoutId.current = undefined;
        };
    }, [token, authenticate]);
}
